import React from "react";
import {
  ActionIcon,
  Box,
  Group,
  Stack,
  Text,
  Title,
  UnstyledButton,
} from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import { useSalon } from "../../context/SalonContext";
import { useLocationSwitcher } from "../../hooks/useLocationSwitcher";
import BrandedLoadingOverlay from "../common/BrandedLoadingOverlay";
import type { AvailableLocation } from "../../types/featureAccess";
import { formatLocationAddress } from "../../utils/currentLocation";
import styles from "./LocationPickerScreen.module.css";

/**
 * Full-page location picker for multi-location staff (PR A / #399).
 *
 * One component, two contexts (both driven by the App-root gate in App.js):
 *  - First sign-in: shown when a multi-location user has no saved selection
 *    (`selectedSalonId == null`). Not dismissible — a location must be chosen
 *    before landing anywhere.
 *  - Manual switch: shown as an overlay when the NavBar building icon opens it.
 *    Dismissible (there is already a current location to fall back to).
 *
 * Identifiers stay `salon`-scoped verbatim (no rename); only UI copy says
 * "location".
 */
export default function LocationPickerScreen() {
  const { availableLocations, selectedSalonId, closeLocationPicker } =
    useSalon() as {
      availableLocations: AvailableLocation[];
      selectedSalonId: number | null;
      closeLocationPicker: () => void;
    };
  const { switchLocation, isSwitching } = useLocationSwitcher();

  // Dismissible only in the manual-switch context — i.e. when a location is
  // already selected. On first sign-in (no selection) the user must pick.
  const dismissible = selectedSalonId != null;

  return (
    <Box className={styles.shell} role="dialog" aria-modal="true">
      {dismissible && (
        <ActionIcon
          variant="subtle"
          color="gray"
          size="lg"
          aria-label="Close location picker"
          onClick={closeLocationPicker}
          pos="absolute"
          top={16}
          right={16}
        >
          <IconX size={22} />
        </ActionIcon>
      )}

      <Stack gap="lg" w="100%" maw={480} align="stretch">
        <Stack gap={4} align="center">
          <Title order={2} ta="center">
            {dismissible ? "Switch location" : "Choose a location"}
          </Title>
          <Text c="dimmed" ta="center">
            {dismissible
              ? "Select which location you want to work in."
              : "You have access to more than one location. Pick where you'd like to start."}
          </Text>
        </Stack>

        <Stack gap="sm">
          {availableLocations.map((loc) => {
            const isSelected = loc.salon_id === selectedSalonId;
            return (
              <UnstyledButton
                key={loc.salon_id}
                className={`${styles.card} ${isSelected ? styles.cardSelected : ""}`}
                p="md"
                onClick={() => switchLocation(loc.salon_id)}
                aria-pressed={isSelected}
                disabled={isSwitching}
              >
                <Group justify="space-between" wrap="nowrap" gap="sm">
                  <Stack gap={2}>
                    <Title order={4}>{loc.name}</Title>
                    <Text size="sm" c="dimmed">
                      {formatLocationAddress(loc)}
                    </Text>
                  </Stack>
                  {isSelected && (
                    <Text size="sm" fw={600} c="var(--brand-teal)">
                      Current
                    </Text>
                  )}
                </Group>
              </UnstyledButton>
            );
          })}
        </Stack>
      </Stack>

      <BrandedLoadingOverlay visible={isSwitching} />
    </Box>
  );
}
